import React from "react";
import { authorizedFetch } from "./boot.js";
import { LotContacts } from "./lot_contacts.jsx";
import { Card, Tag } from "./ui.jsx";
import { sydneyDay } from "./time.js";
import { QrCode } from "./qr_code.jsx";

async function request(path, options = {}) {
  const response = await authorizedFetch(path, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

function post(path, input, key) {
  return request(path, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: key }),
  });
}

const sameValues = (left, right) => JSON.stringify([...left].sort()) === JSON.stringify([...right].sort());
const roleName = role => role === "owner" ? "Owner" : role === "staff" ? "Staff" : role;

// The Actions that carry a staff access link. They share one rule the other
// access writes do not have: the link URL they return is shown once, so a
// failure is answered by reloading the list rather than by leaving a stale URL
// on screen beside a row that may no longer say what it said.
const LINK_ACTIONS = new Set(["issue_link", "reveal_link", "revoke_link", "link_sections"]);

function SectionEditor({ person, sections, locked, onSave, onRemove }) {
  const [chosen, setChosen] = React.useState(() => [...(person.sections || [])]);
  React.useEffect(() => { setChosen([...(person.sections || [])]); }, [person.userId, person.revision]);
  const allSections = person.role === "owner" ? sections.map(item => item.key) : chosen;
  const editable = person.role === "staff" && person.canSetSections && !locked;
  const toggle = key => setChosen(current => current.includes(key)
    ? current.filter(item => item !== key) : [...current, key]);

  return <article className="people-person">
    <div className="people-person-head">
      <div>
        <div className="people-person-name"><strong>{person.displayName}</strong><Tag variant={person.role === "owner" ? "gold" : ""}>{roleName(person.role)}</Tag>
          {person.status !== "active" && <Tag variant="warn">{person.status === "suspended" ? "Suspended" : "Inactive"}</Tag>}
        </div>
        <span className="muted">{person.email || "Email unavailable"}</span>
        {person.isInglisAdmin && <small>Inglis administrator · managed centrally</small>}
        {person.isCurrentUser && <small>This is your account</small>}
      </div>
      {person.canRemove && <button type="button" className="btn sm" disabled={locked}
        onClick={event => onRemove(person, event.currentTarget)}>Remove access</button>}
    </div>
    {editable
      ? <div className="people-section-grid" aria-label={`Sections for ${person.displayName}`}>
          {sections.map(section => <label key={section.key}>
            <input type="checkbox" checked={allSections.includes(section.key)} disabled={locked}
              onChange={() => toggle(section.key)}/>
            <span>{section.label}</span>
          </label>)}
        </div>
      : <div className="people-section-tags" aria-label={`Sections for ${person.displayName}`}>
          {sections.filter(section => allSections.includes(section.key)).map(section =>
            <Tag key={section.key}>{section.label}</Tag>)}
          {!allSections.length && <span className="caption">No dashboard sections assigned</span>}
        </div>}
    {editable && <button type="button" className="btn sm primary"
      disabled={locked || sameValues(chosen, person.sections || [])}
      onClick={() => onSave(person, chosen)}>Save sections</button>}
    {person.role === "owner" && <p className="caption">Owners can use every dashboard section.</p>}
    {!person.canRemove && person.role === "owner" && !person.isInglisAdmin && !person.isCurrentUser
      && <p className="caption">Protected owner access cannot be removed.</p>}
  </article>;
}

/**
 * A live link's sections, changed on the link itself (issue #31). Only the
 * sections a link may reach are offered, in the host's order, and at least one
 * must stay ticked: a link with nothing to open is revoked, not emptied.
 */
function LinkSectionEditor({ row, options, locked, onSave }) {
  const [chosen, setChosen] = React.useState(() => [...row.sections]);
  React.useEffect(() => { setChosen([...row.sections]); }, [row.id, row.revision]);
  const ordered = options.map(option => option.key).filter(key => chosen.includes(key));
  const unchanged = sameValues(ordered, row.sections);
  return <div className="people-link-sections">
    <div className="people-section-grid" aria-label={`Sections for ${row.staffName}'s link`}>
      {options.map(option => <label key={option.key}>
        <input type="checkbox" disabled={locked} checked={chosen.includes(option.key)}
          onChange={() => setChosen(current => current.includes(option.key)
            ? current.filter(item => item !== option.key) : [...current, option.key])}/>
        <span>{option.label}</span>
      </label>)}
    </div>
    <button type="button" className="btn sm" disabled={locked || unchanged || !ordered.length}
      onClick={() => onSave(row, ordered)}>Save sections</button>
  </div>;
}

/**
 * Named, revocable access links for temporary staff.
 *
 * A link is a key, not an account. The URL is shown once per issue or reveal,
 * in a read-only field and as a QR code drawn in the page beside it (issue
 * #32), and nowhere else — never in a notice, a title, an aria attribute or
 * the console — and only the sections the host lists in
 * `linkSections` are offered, because those are the only ones a link may
 * reach. The host refuses the rest regardless; offering them here would be an
 * affordance that promises access it cannot grant.
 */
function StaffLinks({ data, rows, locked, busy, perform, onRevealed, canCreate = true, empty }) {
  const [name, setName] = React.useState("");
  const [sections, setSections] = React.useState([]);
  const options = data.linkSections || [];
  const label = key => (options.find(option => option.key === key) || { label: key }).label;

  // Every Create issues a new link; it is never a retry of the last one. The
  // host mints fresh key material on each attempt, so reusing an idempotency
  // key would reach the runner as the same key with different input and be
  // rejected 409 — the first request could never be replayed. `locked` carries
  // `busy`, so a second click while one is in flight cannot issue twice.
  function create(event) {
    event.preventDefault();
    const trimmed = name.replace(/\s+/g, " ").trim();
    if (!trimmed || !sections.length || data.canIssueLink !== true || busy) return;
    perform({
      kind: "issue_link", path: "/actions/vendor.people.issue_link@1/",
      input: {
        link_id: window.crypto.randomUUID(), staff_name: trimmed,
        sections: [...sections].sort(),
      },
      key: window.crypto.randomUUID(),
      saving: `Creating a link for ${trimmed}…`,
      said: `Link created for ${trimmed}. Copy it below and send it to them.`,
      onResult: result => {
        if (result && result.url) onRevealed(result.url);
        setName(""); setSections([]);
      },
    });
  }

  function reveal(row) {
    perform({
      kind: "reveal_link", path: "/actions/vendor.people.reveal_link@1/",
      input: { link_id: row.id, expected_revision: row.revision },
      key: window.crypto.randomUUID(),
      saving: `Fetching ${row.staffName}'s link…`,
      said: `${row.staffName}'s link is shown below. Copy it and send it to them.`,
      onResult: result => { if (result && result.url) onRevealed(result.url); },
    });
  }

  function revoke(row) {
    if (!window.confirm(`Revoke ${row.staffName}'s link? It stops working immediately.`)) return;
    perform({
      kind: "revoke_link", path: "/actions/vendor.people.revoke_link@1/",
      input: { link_id: row.id, expected_revision: row.revision },
      key: window.crypto.randomUUID(),
      saving: `Revoking ${row.staffName}'s link…`,
      said: `${row.staffName}'s link is revoked.`,
    });
  }

  function saveSections(row, sections) {
    perform({
      kind: "link_sections", path: "/actions/vendor.people.set_link_sections@1/",
      input: { link_id: row.id, sections, expected_revision: row.revision },
      key: window.crypto.randomUUID(),
      saving: `Saving ${row.staffName}'s sections…`,
      said: `${row.staffName}'s link now opens ${sections.map(label).join(", ")}. The link itself is unchanged.`,
    });
  }

  return <div className="people-links">
    {canCreate && <p className="caption">A named link for a temporary staff member. There is no sign-in: it
      works for 30 days or until you revoke it, and it reaches only the sections you tick.</p>}
    {rows.length === 0 && <p className="muted">{empty}</p>}
    {rows.map(row => <div key={row.id} className="people-link-row">
      <span><strong>{row.staffName}</strong>
        <small>{row.sections.map(label).join(" · ")} · expires {sydneyDay(row.expiresAt)}</small></span>
      <Tag>{row.status === "active" ? "Active" : row.status === "revoked" ? "Revoked" : "Expired"}</Tag>
      {row.canReveal && <button type="button" className="btn sm" disabled={locked}
        onClick={() => reveal(row)}>Show link</button>}
      {row.canRevoke && <button type="button" className="btn sm" disabled={locked}
        onClick={() => revoke(row)}>Revoke</button>}
      {row.canSetSections && <LinkSectionEditor row={row} options={options} locked={locked}
        onSave={saveSections}/>}
    </div>)}
    {canCreate && data.canIssueLink === true && <form className="people-invite" onSubmit={create}>
      <label>Staff member&apos;s name
        <input value={name} maxLength={120} disabled={locked}
          onChange={event => setName(event.target.value)}/>
      </label>
      <fieldset><legend>Sections</legend><div className="people-section-grid">
        {options.map(option => <label key={option.key}>
          <input type="checkbox" disabled={locked} checked={sections.includes(option.key)}
            onChange={() => setSections(current => current.includes(option.key)
              ? current.filter(item => item !== option.key) : [...current, option.key])}/>
          <span>{option.label}</span>
        </label>)}
      </div></fieldset>
      <button type="submit" className="btn primary"
        disabled={locked || !name.trim() || !sections.length}>Create link</button>
    </form>}
    {canCreate && data.canIssueLink === false
      && <p className="caption">Staff links cannot be created in this environment yet.</p>}
  </div>;
}

export function AdminView({ sale = window.VD.lotContacts?.sale || "26R2R",
  vendor = window.VD.access.realm }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const [retry, setRetry] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [inviteContact, setInviteContact] = React.useState(null);
  const [inviteRole, setInviteRole] = React.useState("staff");
  const [inviteSections, setInviteSections] = React.useState([]);
  const [inviteLink, setInviteLink] = React.useState("");
  const [linkUrl, setLinkUrl] = React.useState("");
  const [removing, setRemoving] = React.useState(null);
  const [tab, setTab] = React.useState("people");
  const mounted = React.useRef(true);
  const busyRef = React.useRef(false);
  const inviteAttempt = React.useRef(null);
  const inviteFormRef = React.useRef(null);
  const inviteReturnFocus = React.useRef(null);
  const inviteLinkRef = React.useRef(null);
  const removeDialogRef = React.useRef(null);
  const removeReturnFocus = React.useRef(null);
  const readPath = `/collections/vendor.people.list@1/?${new URLSearchParams({ vendor, sale })}`;

  const load = React.useCallback(async () => {
    const value = await request(readPath);
    if (!mounted.current) return;
    setData(value); setRefreshRequired(false); setRetry(null);
  }, [readPath]);

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    load().catch(() => { if (active) setError("People could not be loaded. Refresh to try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; };
  }, [load]);

  React.useEffect(() => {
    if (!inviteContact) return undefined;
    const focusInvite = () => {
      inviteFormRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      inviteFormRef.current?.querySelector("select")?.focus();
    };
    const frame = window.requestAnimationFrame
      ? window.requestAnimationFrame(focusInvite) : window.setTimeout(focusInvite, 0);
    return () => {
      if (window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
    };
  }, [inviteContact]);

  React.useEffect(() => {
    if (!inviteLink) return undefined;
    const focusLink = () => {
      inviteLinkRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      inviteLinkRef.current?.focus();
      inviteLinkRef.current?.select();
    };
    const frame = window.requestAnimationFrame
      ? window.requestAnimationFrame(focusLink) : window.setTimeout(focusLink, 0);
    return () => {
      if (window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
    };
  }, [inviteLink]);

  React.useEffect(() => {
    if (!removing) return undefined;
    const focusConfirmation = () => {
      removeDialogRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      removeDialogRef.current?.querySelector("button")?.focus();
    };
    const frame = window.requestAnimationFrame
      ? window.requestAnimationFrame(focusConfirmation) : window.setTimeout(focusConfirmation, 0);
    return () => {
      if (window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
    };
  }, [removing]);

  // Inviting starts in the lot-contacts column, and revealing a link starts in
  // the staff links list. Whichever tab the Owner is on, the form or the URL
  // they just asked for has to be the thing in front of them.
  React.useEffect(() => { if (inviteContact || inviteLink) setTab("invitations"); }, [inviteContact, inviteLink]);
  React.useEffect(() => { if (linkUrl) setTab("links"); }, [linkUrl]);

  function closeInvite(restoreFocus = false) {
    setInviteContact(null); inviteAttempt.current = null;
    if (restoreFocus) {
      const trigger = inviteReturnFocus.current;
      const frame = window.requestAnimationFrame
        ? window.requestAnimationFrame(() => trigger?.isConnected && trigger.focus())
        : window.setTimeout(() => trigger?.isConnected && trigger.focus(), 0);
      return frame;
    }
    return null;
  }

  function openRemove(person, trigger) {
    removeReturnFocus.current = trigger;
    setRemoving(person);
  }

  function closeRemove() {
    const trigger = removeReturnFocus.current;
    setRemoving(null);
    const frame = window.requestAnimationFrame
      ? window.requestAnimationFrame(() => trigger?.isConnected && trigger.focus())
      : window.setTimeout(() => trigger?.isConnected && trigger.focus(), 0);
    return frame;
  }

  async function refresh() {
    if (busyRef.current) return;
    setLoading(true); setError(""); setNotice(""); setRemoving(null);
    try { await load(); }
    catch (_) { if (mounted.current) setError("People could not be loaded. Refresh to try again."); }
    finally { if (mounted.current) setLoading(false); }
  }

  /** Reload People after a link write whose outcome the caller cannot retry. */
  async function reloadLinks(message) {
    try {
      await load();
      if (mounted.current) setNotice(message);
    } catch (_) {
      if (mounted.current) {
        setRefreshRequired(true);
        setError("People could not be reloaded. Refresh before making another change.");
      }
    }
  }

  async function perform(attempt) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(""); setNotice(attempt.saving);
    // A shown staff link belongs to the row it came from, and any further link
    // write can end or replace that row. The URL leaves the screen first and
    // is put back only by the result that produced it.
    if (LINK_ACTIONS.has(attempt.kind)) setLinkUrl("");
    try {
      const result = await post(attempt.path, attempt.input, attempt.key);
      if (!mounted.current) return;
      setRetry(null); setRemoving(null); setRefreshRequired(true);
      let successNotice = attempt.said;
      // A one-time URL is handed to the caller before the reload, which is the
      // only place it goes: not into the notice, not into the console.
      if (attempt.onResult) attempt.onResult(result);
      if (attempt.kind === "invite") {
        setInviteLink(result.shareUrl || "");
        setInviteContact(null); inviteAttempt.current = null;
        successNotice = result.deliveryQueued === false
          ? `${attempt.said} Copy the link to share it.`
          : `${attempt.said} Email delivery is queued.`;
      }
      try {
        await load();
        if (mounted.current) setNotice(successNotice);
      } catch (_) {
        if (mounted.current) setError("The change was saved, but People could not be reloaded. Refresh before making another change.");
      }
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if (attempt.kind === "issue_link") {
        // Nothing here can be retried: the host mints fresh key material on
        // every attempt, so the same idempotency key would arrive with
        // different input and be rejected. The link may still have been
        // issued, so the list is reloaded and Show link is the way back to it.
        setRetry(null); setRemoving(null);
        await reloadLinks("The link could not be confirmed. If it appears in the list below, use Show link.");
      } else if (LINK_ACTIONS.has(attempt.kind) && failure.status === 412) {
        // The row was fenced on the revision this list was rendered from, and
        // that revision is stale — the link was revoked, expired or changed
        // elsewhere. Reloading shows what it says now.
        setRetry(null); setRemoving(null);
        await reloadLinks("The staff links list has changed. It has been reloaded — check it and try again.");
      } else if (failure.message === "delivery_unavailable") {
        setRetry(null);
        setError("Invitation email is not available. No invitation was created.");
      } else if ([400, 404, 409, 412].includes(failure.status)) {
        setRetry(null); setRefreshRequired(true); setRemoving(null);
        setError(failure.status === 409 || failure.status === 412
          ? "Access changed somewhere else. Refresh before saving again."
          : "This access change could not be accepted. Refresh and review People before trying again.");
      } else {
        setRetry(attempt);
        setError("The request could not be confirmed. Retry this exact request before making another change.");
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  function saveSections(person, sections) {
    perform({
      kind: "sections", path: "/actions/vendor.people.set_sections@1/",
      input: { user_id: person.userId, sections: [...sections].sort(), expected_revision: person.revision },
      key: window.crypto.randomUUID(), saving: `Saving ${person.displayName}'s sections…`,
      said: `${person.displayName}'s sections are saved.`,
    });
  }

  function removePerson(person) {
    perform({
      kind: "remove", path: "/actions/vendor.people.remove@1/",
      input: { user_id: person.userId, expected_revision: person.revision },
      key: window.crypto.randomUUID(), saving: `Removing ${person.displayName}'s access…`,
      said: `${person.displayName} no longer has access to this vendor.`,
    });
  }

  function invite(event) {
    event.preventDefault();
    if (!inviteContact || data?.canInvite !== true || data?.deliveryReady !== true || busyRef.current) return;
    const input = {
      invitation_id: inviteAttempt.current?.input?.invitation_id || window.crypto.randomUUID(),
      contact_id: inviteContact.id,
      role: inviteRole,
      sections: inviteRole === "owner" ? [] : [...inviteSections].sort(),
    };
    const signature = JSON.stringify({ ...input, invitation_id: null });
    if (!inviteAttempt.current || inviteAttempt.current.signature !== signature) {
      inviteAttempt.current = {
        kind: "invite", signature, input: { ...input, invitation_id: window.crypto.randomUUID() },
        path: "/actions/vendor.people.invite@1/", key: window.crypto.randomUUID(),
        saving: `Inviting ${inviteContact.name}…`, said: `Invitation created for ${inviteContact.name}.`,
      };
    }
    perform(inviteAttempt.current);
  }

  const locked = loading || busy || refreshRequired || Boolean(retry);
  const sections = data?.sections || [];

  // Access that has ended is not current access. Someone whose access was
  // removed, and a link that was revoked or has expired, leave the lists an
  // Owner works from — they are the record of what happened, not the answer to
  // "who can see my horses". They stay reachable under Past access rather than
  // being destroyed on screen, because an Owner who removed the wrong person
  // needs to be able to see that they did.
  const people = data?.people || [];
  const currentPeople = people.filter(person => person.status === "active");
  const pastPeople = people.filter(person => person.status !== "active");
  const links = data?.links || [];
  const currentLinks = links.filter(row => row.status === "active");
  const pastLinks = links.filter(row => row.status !== "active");
  const pending = (data?.invitations || []).filter(row => row.status === "pending");
  const pastCount = pastPeople.length + pastLinks.length;

  const tabs = [
    { id: "people", label: "People", count: currentPeople.length },
    { id: "invitations", label: "Invitations", count: pending.length },
    { id: "links", label: "Staff links", count: currentLinks.length },
    ...(pastCount ? [{ id: "past", label: "Past access", count: pastCount }] : []),
  ];
  const tabIds = tabs.map(item => item.id).join(",");

  // Past access is the one tab that can stop existing — restore the last
  // revoked link and the tab goes with it. Without this the Owner is left
  // looking at an empty panel with no tab selected.
  React.useEffect(() => {
    if (!tabIds.split(",").includes(tab)) setTab("people");
  }, [tabIds, tab]);
  const contactAccess = {};
  (data?.people || []).forEach(person => {
    if (person.contactId) contactAccess[person.contactId] = {
      label: person.status === "active"
        ? `${roleName(person.role)} access active`
        : `${roleName(person.role)} access ${person.status === "suspended" ? "suspended" : "inactive"}`,
      canReinvite: person.canReinvite === true,
    };
  });
  (data?.invitations || []).forEach(row => {
    if (row.contactId && row.status === "pending" && !contactAccess[row.contactId]) {
      contactAccess[row.contactId] = { label: `${roleName(row.role)} invitation pending`, canReinvite: false };
    }
  });

  return <div className="people-view fade-in">
    <div className="people-layout">
      <Card title="Dashboard access" subtitle={`${data?.vendorName || vendor} · Owners and staff`}>
        <div className="people-toolbar">
          <button type="button" className="btn sm" disabled={busy || loading} onClick={refresh}>Refresh people</button>
          {retry && <button type="button" className="btn sm primary" disabled={busy}
            onClick={() => perform(retry)}>Retry same request</button>}
        </div>
        {loading && <p role="status">Loading people…</p>}
        {error && <p role="alert" className="error-text">{error}</p>}
        {notice && <p role="status" aria-live="polite" className="report-notice">{notice}</p>}
        {data && !data.canInvite && !data.canSetSections && !data.canRemove
          && <p className="caption">You can view People, but your access does not allow changes.</p>}
        {data && <div className="people-tabs" role="tablist" aria-label="Dashboard access">
          {tabs.map(item => <button key={item.id} type="button" role="tab" id={`people-tab-${item.id}`}
            aria-selected={tab === item.id} aria-controls={`people-panel-${item.id}`}
            className={tab === item.id ? "active" : ""} onClick={() => setTab(item.id)}>
            {item.label}<span className="people-tab-count">{item.count}</span>
          </button>)}
        </div>}

        {removing && <div ref={removeDialogRef} className="people-confirm" role="alertdialog" aria-label={`Remove ${removing.displayName}`}>
          <p>Remove {removing.displayName}&apos;s access to {data?.vendorName || vendor}? Their access to other vendors will stay unchanged.</p>
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn sm primary" disabled={locked} onClick={() => removePerson(removing)}>Yes, remove access</button>
            <button type="button" className="btn sm" disabled={busy} onClick={closeRemove}>Keep access</button>
          </div>
        </div>}

        {data && tab === "people" && <div role="tabpanel" id="people-panel-people" aria-labelledby="people-tab-people">
          {!currentPeople.length && <p className="muted">No one has dashboard access yet.</p>}
          <div className="people-list">
            {currentPeople.map(person => <SectionEditor key={person.userId} person={person}
              sections={sections} locked={locked} onSave={saveSections} onRemove={openRemove}/>)}
          </div>
        </div>}

        {data && tab === "invitations" && <div role="tabpanel" id="people-panel-invitations" aria-labelledby="people-tab-invitations">
          {!pending.length && !inviteContact && !inviteLink
            && <p className="muted">No invitation is waiting to be accepted. Invite a lot contact from the list beside this one.</p>}

          {inviteContact && <form ref={inviteFormRef} className="people-invite" onSubmit={invite}>
            <div className="people-invite-head"><div><span className="eyebrow">Invite contact</span><h4>{inviteContact.name}</h4><span className="muted">{inviteContact.email}</span></div>
              <button type="button" className="btn sm" disabled={busy} onClick={() => closeInvite(true)}>Cancel</button></div>
            <label>Dashboard role
              <select value={inviteRole} disabled={locked} onChange={event => {
                setInviteRole(event.target.value); setInviteSections([]); inviteAttempt.current = null;
              }}>
                <option value="staff">Staff</option><option value="owner">Owner</option>
              </select>
            </label>
            {inviteRole === "staff" && <fieldset><legend>Sections</legend><div className="people-section-grid">
              {sections.map(section => <label key={section.key}><input type="checkbox" disabled={locked}
                checked={inviteSections.includes(section.key)} onChange={() => {
                  setInviteSections(current => current.includes(section.key)
                    ? current.filter(item => item !== section.key) : [...current, section.key]);
                  inviteAttempt.current = null;
                }}/><span>{section.label}</span></label>)}
            </div></fieldset>}
            {inviteRole === "owner" && <p className="caption">Owners receive every dashboard section and can manage People.</p>}
            <button type="submit" className="btn primary" disabled={locked || data?.canInvite !== true || data?.deliveryReady !== true}>Create invitation</button>
            {data?.deliveryReady === false && <p role="status" className="error-text">Invitation email is not available. No invitation can be created yet.</p>}
          </form>}

          {inviteLink && <div className="people-share">
            <label htmlFor="people-invite-link">Invitation link</label>
            <input ref={inviteLinkRef} id="people-invite-link" value={inviteLink} readOnly autoComplete="off"/>
            <button type="button" className="btn sm" onClick={async () => {
              try { await navigator.clipboard.writeText(inviteLink); setNotice("Invitation link copied."); }
              catch (_) { setNotice("Select the invitation link and copy it manually."); }
            }}>Copy link</button>
            <figure className="people-qr">
              <QrCode value={inviteLink} label="QR code for this invitation link"/>
              <figcaption className="caption">They can scan this with their phone&apos;s camera to open the invitation.</figcaption>
            </figure>
          </div>}

          {pending.length > 0 && <div className="people-pending">
            <h4>Pending invitations</h4>
            {pending.map(row => <div key={row.id}>
              <span><strong>{row.contactName}</strong><small>{row.email} · {roleName(row.role)}</small></span>
              <Tag>Pending</Tag>
            </div>)}
          </div>}
        </div>}

        {data && tab === "links" && <div role="tabpanel" id="people-panel-links" aria-labelledby="people-tab-links">
          <StaffLinks data={data} rows={currentLinks} locked={locked} busy={busy} perform={perform}
            onRevealed={setLinkUrl} empty="No staff links yet."/>
          {linkUrl && <div className="people-share">
            <label htmlFor="people-staff-link">Staff link</label>
            <input id="people-staff-link" value={linkUrl} readOnly autoComplete="off"/>
            <button type="button" className="btn sm" onClick={async () => {
              try { await navigator.clipboard.writeText(linkUrl); setNotice("Staff link copied."); }
              catch (_) { setNotice("Select the staff link and copy it manually."); }
            }}>Copy link</button>
            <figure className="people-qr">
              <QrCode value={linkUrl} label="QR code for this staff link"/>
              <figcaption className="caption">They can scan this with their phone&apos;s camera to open the link.</figcaption>
            </figure>
          </div>}
        </div>}

        {data && tab === "past" && <div role="tabpanel" id="people-panel-past" aria-labelledby="people-tab-past">
          <p className="caption">Access that has ended. Nothing here can see this vendor&apos;s horses.</p>
          {pastPeople.length > 0 && <>
            <h4>Removed people</h4>
            <div className="people-list">
              {pastPeople.map(person => <SectionEditor key={person.userId} person={person}
                sections={sections} locked={locked} onSave={saveSections} onRemove={openRemove}/>)}
            </div>
          </>}
          {pastLinks.length > 0 && <>
            <h4>Revoked and expired links</h4>
            <StaffLinks data={data} rows={pastLinks} locked={locked} busy={busy} perform={perform}
              onRevealed={setLinkUrl} canCreate={false} empty=""/>
          </>}
        </div>}

      </Card>

      <LotContacts sale={sale} vendor={vendor} accessByContact={contactAccess}
        onInvite={data?.canInvite === true ? ((contact, trigger) => {
          inviteReturnFocus.current = trigger;
          setInviteContact(contact); setInviteRole("staff"); setInviteSections([]);
          setInviteLink(""); setLinkUrl(""); setError(""); setNotice("");
          inviteAttempt.current = null;
        }) : null}/>
    </div>
  </div>;
}
